import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';

const GoogleOAuthCallback = ({ token }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('processing');
  const [message, setMessage] = useState('Connecting to Google Calendar...');
  const hasRun = useRef(false);

  useEffect(() => {
    // Prevent double execution in strict mode
    if (hasRun.current) return;
    hasRun.current = true;

    const processCallback = async () => {
      const code = searchParams.get('code');
      const state = searchParams.get('state');
      const error = searchParams.get('error');

      if (!token) {
        setStatus('error');
        setMessage('Not authenticated. Please log in and try again.');
        setTimeout(() => navigate('/login'), 3000);
        return;
      }

      if (error) {
        setStatus('error');
        setMessage(`Authorization denied: ${error}`);
        setTimeout(() => {
          const returnPath = localStorage.getItem('gcal_oauth_return') || '/admin';
          localStorage.removeItem('gcal_oauth_return');
          navigate(returnPath);
        }, 3000);
        return;
      }

      if (!code || !state) {
        setStatus('error');
        setMessage('Missing authorization code or state parameter');
        setTimeout(() => navigate('/admin'), 3000);
        return;
      }

      try {
        const redirectUri = `${window.location.origin}/auth/google/callback`;
        
        const response = await axios.post(
          `${window.location.origin}/api/integrations/google-calendar/callback`,
          {
            code,
            state,
            redirect_uri: redirectUri
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.data.success) {
          setStatus('success');
          setMessage('Google Calendar connected successfully!');
        } else {
          setStatus('error');
          setMessage(response.data.error || 'Failed to connect Google Calendar');
        }
      } catch (err) {
        console.error('OAuth callback error:', err);
        setStatus('error');
        setMessage(err.response?.data?.detail || 'Failed to complete authorization');
      }

      // Redirect back after a delay
      setTimeout(() => {
        const returnPath = localStorage.getItem('gcal_oauth_return') || '/admin';
        localStorage.removeItem('gcal_oauth_return');
        navigate(returnPath + '?tab=integrations');
      }, 2000);
    };

    processCallback();
  }, [searchParams, token, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full text-center">
        {status === 'processing' && (
          <>
            <Loader2 className="w-16 h-16 text-blue-600 animate-spin mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Connecting...</h2>
            <p className="text-gray-600">{message}</p>
          </>
        )}
        
        {status === 'success' && (
          <>
            <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Success!</h2>
            <p className="text-gray-600">{message}</p>
            <p className="text-sm text-gray-500 mt-4">Redirecting you back...</p>
          </>
        )}
        
        {status === 'error' && (
          <>
            <XCircle className="w-16 h-16 text-red-600 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Connection Failed</h2>
            <p className="text-gray-600">{message}</p>
            <p className="text-sm text-gray-500 mt-4">Redirecting you back...</p>
          </>
        )}
      </div>
    </div>
  );
};

export default GoogleOAuthCallback;
